import * as THREE from "three"
import BaseThreeScene from "../../scenes/BaseThreeScene"
import { createDirectedGraphEdge, createGraphNode } from "../../scenes/graphPrimitives"
import { createTextSprite, updateTextSprite } from "../../scenes/shared/textSpritePrimitives"
import { buildDirectedAdjacency, buildIndegrees, createLayeredDirectedGraphLayout } from "../../scenes/shared/graphLayout"

const DEFAULT_COURSE_INPUT = {
    courseCount: 8,
    prerequisites: [
        [2, 0],
        [2, 1],
        [3, 1],
        [4, 2],
        [5, 2],
        [5, 3],
        [6, 4],
        [6, 5],
        [7, 5],
    ],
};

const GRAPH_SCALE = 1.05;
const GRAPH_OFFSET = new THREE.Vector3(1.35, 0, 0);

const BACKDROP_Z = -2.3;
const LANE_Z = -0.9;
const LANE_HEIGHT = 0.68;

const NODE_IDLE_COLOR = new THREE.Color(0x334155);
const NODE_AVAILABLE_COLOR = new THREE.Color(0x60a5fa);
const NODE_ACTIVE_COLOR = new THREE.Color(0xe0f2fe);
const NODE_COMPLETE_COLOR = new THREE.Color(0x34d399);
const NODE_LOCKED_COLOR = new THREE.Color(0x1e293b);

const EDGE_IDLE_COLOR = new THREE.Color(0x334155);
const EDGE_ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const EDGE_RESOLVED_COLOR = new THREE.Color(0x22c55e);

function edgeKey(fromId, toId) {
    return `${fromId}:${toId}`;
}

function normalizeCourseInput(input) {
    const source = input || DEFAULT_COURSE_INPUT;

    return {
        courseCount: source.courseCount,
        prerequisites: source.prerequisites.map((edge) => edge.slice()),
    };
}

function buildCourseScheduleSteps(courseInput) {
    const { courseCount, prerequisites } = courseInput;
    const adjacency = buildDirectedAdjacency(Array.from({ length: courseCount }, (_, index) => index), prerequisites);
    const indegrees = buildIndegrees(courseCount, prerequisites);
    const queue = [];
    const steps = [];
    let completedCount = 0;

    for (let courseId = 0; courseId < courseCount; courseId += 1) {
        if (indegrees[courseId] === 0) {
            queue.push(courseId);
            steps.push({
                type: "seed-available",
                courseId,
            });
        }
    }

    queue.sort((a, b) => a - b);

    while (queue.length > 0) {
        const courseId = queue.shift();

        steps.push({
            type: "dequeue-course",
            courseId,
        });

        steps.push({
            type: "take-course",
            courseId,
        });

        for (const nextCourseId of adjacency.get(courseId)) {
            const before = indegrees[nextCourseId];
            indegrees[nextCourseId] -= 1;
            const after = indegrees[nextCourseId];

            steps.push({
                type: "decrement-indegree",
                fromId: courseId,
                toId: nextCourseId,
                before,
                after,
            });

            if (after === 0) {
                queue.push(nextCourseId);
                queue.sort((a, b) => a - b);
                steps.push({
                    type: "enqueue-course",
                    courseId: nextCourseId,
                    fromId: courseId,
                });
            }
        }

        completedCount += 1;
        steps.push({
            type: "complete-course",
            courseId,
        });
    }

    steps.push({
        type: "complete",
        canFinish: completedCount === courseCount,
        completedCount,
    });

    return steps;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1

    switch (step.type) {
        case "seed-available":
            return {
                id: `course-schedule-step-${stepNumber}-seed-${step.courseId}`,
                stepIndex: stepNumber,
                kind: "Seed",
                title: `Seed course ${step.courseId}`,
                message: `Course ${step.courseId} starts with indegree 0, so Kahn's algorithm places it into the available queue.`,
            }

        case "dequeue-course":
            return {
                id: `course-schedule-step-${stepNumber}-dequeue-${step.courseId}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Dequeue course ${step.courseId}`,
                message: `Course ${step.courseId} is removed from the front of the queue because all of its prerequisites are already satisfied.`,
            }

        case "take-course":
            return {
                id: `course-schedule-step-${stepNumber}-take-${step.courseId}`,
                stepIndex: stepNumber,
                kind: "Take",
                title: `Take course ${step.courseId}`,
                message: `The algorithm commits course ${step.courseId} into the topological ordering and begins relaxing its outgoing edges.`,
            }

        case "decrement-indegree":
            return {
                id: `course-schedule-step-${stepNumber}-decrement-${step.fromId}-${step.toId}`,
                stepIndex: stepNumber,
                kind: "Relax",
                title: `Relax edge ${step.fromId} -> ${step.toId}`,
                message: `Completing course ${step.fromId} reduces course ${step.toId}'s remaining indegree from ${step.before} to ${step.after}.`,
            }

        case "enqueue-course":
            return {
                id: `course-schedule-step-${stepNumber}-enqueue-${step.courseId}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Enqueue course ${step.courseId}`,
                message: `Course ${step.courseId} now has indegree 0 and becomes available after resolving the edge from course ${step.fromId}.`,
            }

        case "complete-course":
            return {
                id: `course-schedule-step-${stepNumber}-complete-course-${step.courseId}`,
                stepIndex: stepNumber,
                kind: "Done",
                title: `Finish course ${step.courseId}`,
                message: `Course ${step.courseId} is locked into the final order and no longer blocks any downstream work.`,
            }

        case "complete":
            return {
                id: `course-schedule-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: step.canFinish ? "Schedule complete" : "Cycle detected",
                message: step.canFinish
                    ? `All ${step.completedCount} courses were scheduled successfully, so the graph is acyclic and can be finished.`
                    : `Only ${step.completedCount} courses could be scheduled, which indicates a cycle blocked the remaining prerequisites.`,
            }

        default:
            return {
                id: `course-schedule-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the topological sort visualization to the next state.",
            }
    }
}

export default class CourseScheduleScene extends BaseThreeScene {
    setup(props) {
        this.courseInput = normalizeCourseInput(props.input);
        this.courseCount = this.courseInput.courseCount;
        this.prerequisites = this.courseInput.prerequisites.map((edge) => edge.slice());
        this.courseLayout = createLayeredDirectedGraphLayout(this.courseCount, this.prerequisites);
        this.laneYValues = Array.from(
            new Set(this.courseLayout.map((node) => node.position.y * GRAPH_SCALE))
        ).sort((a, b) => b - a);

        const maxAbsCourseX = Math.max(
            ...this.courseLayout.map((node) => Math.abs(node.position.x * GRAPH_SCALE)),
            1
        )
        const maxAbsCourseY = Math.max(
            ...this.courseLayout.map((node) => Math.abs(node.position.y * GRAPH_SCALE)),
            1
        )

        this.scene.background = new THREE.Color(0x050816)
        this.scene.fog = new THREE.Fog(0x050816, 12, 22)
        this.camera.position.set(0.15, 0, 7.8 + maxAbsCourseX * 0.62 + maxAbsCourseY * 0.58)

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05)
        this.scene.add(ambientLight)

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7)
        keyLight.position.set(4, 8, 10)
        this.scene.add(keyLight)

        const leftLight = new THREE.PointLight(0x60a5fa, 15, 16, 2)
        leftLight.position.set(-5.5, 2.5, 5)
        this.scene.add(leftLight)

        const rightLight = new THREE.PointLight(0x22c55e, 14, 16, 2)
        rightLight.position.set(4.5, -1.5, 4)
        this.scene.add(rightLight)

        this.graphRoot = new THREE.Group()
        this.scene.add(this.graphRoot)

        this.courseNodes = new Map()
        this.coursePositions = new Map()
        this.courseLabels = new Map()
        this.indegreeBadges = new Map()
        this.courseEdges = new Map()

        this.backdrop = null
        this.laneMeshes = []
        this.presentationTime = 0
        this.lastBackdropAspect = null

        this.buildBackdrop()
        this.buildGraph()
        this.buildIndicators()
        this.resetAnimationState()
        this.advanceStep()
    }

    getViewportSizeAtZ(worldZ) {
        const distance = Math.abs(this.camera.position.z - worldZ)
        const verticalFovRadians = THREE.MathUtils.degToRad(this.camera.fov)
        const height = 2 * Math.tan(verticalFovRadians * 0.5) * distance
        const width = height * this.camera.aspect

        return { width, height }
    }

    buildBackdrop() {
        this.backdrop = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.75,
            })
        )
        this.backdrop.position.set(this.camera.position.x, 0, BACKDROP_Z)
        this.backdrop.renderOrder = -20
        this.scene.add(this.backdrop)

        const laneMaterial = new THREE.MeshBasicMaterial({
            color: 0x0f172a,
            transparent: true,
            opacity: 0.95,
        })

        this.laneMeshes = this.laneYValues.map((y) => {
            const lane = new THREE.Mesh(
                new THREE.PlaneGeometry(1, 1),
                laneMaterial.clone()
            )
            lane.position.set(this.camera.position.x, y, LANE_Z)
            lane.renderOrder = -10
            lane.userData.baseY = y
            this.scene.add(lane)
            return lane
        })

        this.updateBackdropLayout()
    }

    updateBackdropLayout() {
        if (!this.backdrop) {
            return
        }

        const backdropSize = this.getViewportSizeAtZ(BACKDROP_Z)
        this.backdrop.position.x = this.camera.position.x
        this.backdrop.scale.set(
            backdropSize.width * 1.06,
            backdropSize.height * 1.06,
            1
        )

        const laneSize = this.getViewportSizeAtZ(LANE_Z)
        const laneWidth = laneSize.width * 1.08

        this.laneMeshes.forEach((lane) => {
            lane.position.x = this.camera.position.x
            lane.scale.set(laneWidth, LANE_HEIGHT, 1)
        })

        this.lastBackdropAspect = this.camera.aspect
    }

    onResize() {
        this.updateBackdropLayout()
    }

    buildGraph() {
        for (const nodeData of this.courseLayout) {
            const position = nodeData.position
                .clone()
                .multiplyScalar(GRAPH_SCALE)
                .add(GRAPH_OFFSET)

            this.coursePositions.set(nodeData.id, position)

            const node = createGraphNode({
                id: nodeData.id,
                position,
                radius: 0.34,
                color: NODE_IDLE_COLOR.getHex(),
            })

            node.userData.baseY = position.y
            this.courseNodes.set(nodeData.id, node)
            this.graphRoot.add(node)

            const label = createTextSprite({
                text: nodeData.id,
                width: 128,
                height: 56,
                fontSize: 24,
                textColor: "#ffffff",
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: "rgba(2, 6, 23, 0.92)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
                scale: [0.8, 0.34],
            })
            label.position.copy(position.clone().add(new THREE.Vector3(0, 0, 0.44)))
            label.renderOrder = 25
            this.courseLabels.set(nodeData.id, label)
            this.graphRoot.add(label)

            const indegreeBadge = createTextSprite({
                text: "0",
                width: 120,
                height: 70,
                fontSize: 30,
                textColor: "#dbeafe",
                fillStyle: "rgba(20, 20, 20, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.16)",
                outlineColor: "rgba(0, 0, 0, 0.96)",
                outlineWidth: 9,
                shadowColor: "rgba(0, 0, 0, 0.45)",
                shadowBlur: 7,
                scale: [0.68, 0.4],
            })
            indegreeBadge.position.copy(position.clone().add(new THREE.Vector3(0, 0.84, 0.08)))
            indegreeBadge.renderOrder = 26
            this.indegreeBadges.set(nodeData.id, indegreeBadge)
            this.graphRoot.add(indegreeBadge)
        }

        for (const [courseId, prerequisiteId] of this.prerequisites) {
            const edge = createDirectedGraphEdge({
                fromId: prerequisiteId,
                toId: courseId,
                start: this.coursePositions.get(prerequisiteId),
                end: this.coursePositions.get(courseId),
                color: EDGE_IDLE_COLOR.getHex(),
            })
            this.courseEdges.set(edgeKey(prerequisiteId, courseId), edge)
            this.graphRoot.add(edge)
        }
    }

    buildIndicators() {
        this.activeRing = new THREE.Mesh(
            new THREE.RingGeometry(0.42, 0.5, 48),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.92,
                side: THREE.DoubleSide,
            })
        )
        this.activeRing.rotation.x = Math.PI * 0.5
        this.graphRoot.add(this.activeRing)

        this.edgePulse = new THREE.Mesh(
            new THREE.SphereGeometry(0.13, 18, 18),
            new THREE.MeshBasicMaterial({
                color: 0xfbbf24,
                transparent: true,
                opacity: 0,
            })
        )
        this.scene.add(this.edgePulse)
    }

    resetAnimationState() {
        this.adjacency = buildDirectedAdjacency(Array.from({ length: this.courseCount }, (_, index) => index), this.prerequisites)
        this.baseIndegrees = buildIndegrees(this.courseCount, this.prerequisites)
        this.remainingIndegrees = this.baseIndegrees.slice()
        this.steps = buildCourseScheduleSteps(this.courseInput)
        this.stepIndex = -1
        this.currentStep = null
        this.currentStepElapsed = 0

        this.activeCourseId = null
        this.activeEdgeKey = null
        this.courseQueue = []
        this.availableCourseIds = new Set()
        this.completedCourseIds = new Set()
        this.resolvedEdgeKeys = new Set()
        this.topologicalOrder = []
        this.canFinish = null

        this.edgePulseState = {
            active: false,
            from: new THREE.Vector3(),
            to: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.8,
        }

        this.presentationTime = 0
        this.resetStepLog([])

        this.replaceUiState({
            activeCourseId: null,
            queue: [],
            completedOrder: [],
            availableCount: 0,
            completedCount: 0,
            remainingCount: this.courseCount,
            canFinish: null,
            indegrees: this.remainingIndegrees.map((value, courseId) => ({
                courseId,
                value,
            })),
        })

        this.syncPlaybackState()
    }

    syncUiState() {
        this.replaceUiState({
            activeCourseId: this.activeCourseId,
            queue: this.courseQueue.slice(),
            completedOrder: this.topologicalOrder.slice(),
            availableCount: this.availableCourseIds.size,
            completedCount: this.completedCourseIds.size,
            remainingCount: this.courseCount - this.completedCourseIds.size,
            canFinish: this.canFinish,
            indegrees: this.remainingIndegrees.map((value, courseId) => ({
                courseId,
                value,
            })),
        })
    }

    getStepDuration(step) {
        switch (step.type) {
            case "seed-available":
                return 0.7
            case "dequeue-course":
                return 0.7
            case "take-course":
                return 0.9
            case "decrement-indegree":
                return 0.82
            case "enqueue-course":
                return 0.72
            case "complete-course":
                return 0.7
            case "complete":
                return 2.3
            default:
                return 0.8
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        })
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1
        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState()
            this.syncUiState()
            return false
        }

        this.stepIndex = nextStepIndex
        this.currentStepElapsed = 0
        this.currentStep = this.steps[this.stepIndex] || null
        this.applyStep(this.currentStep)
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex))
        this.syncPlaybackState()
        this.syncUiState()

        return true
    }

    applyStep(step) {
        if (!step) {
            return
        }

        this.activeEdgeKey = null

        switch (step.type) {
            case "seed-available": {
                if (!this.courseQueue.includes(step.courseId)) {
                    this.courseQueue.push(step.courseId)
                }
                this.availableCourseIds.add(step.courseId)
                this.activeCourseId = step.courseId
                break
            }

            case "dequeue-course": {
                this.activeCourseId = step.courseId
                if (this.courseQueue.length > 0 && this.courseQueue[0] === step.courseId) {
                    this.courseQueue.shift()
                } else {
                    this.courseQueue = this.courseQueue.filter(
                        (courseId) => courseId !== step.courseId
                    )
                }
                this.availableCourseIds.delete(step.courseId)
                break
            }

            case "take-course": {
                this.activeCourseId = step.courseId
                break
            }

            case "decrement-indegree": {
                this.activeCourseId = step.fromId
                this.activeEdgeKey = edgeKey(step.fromId, step.toId)
                this.remainingIndegrees[step.toId] = step.after
                this.resolvedEdgeKeys.add(this.activeEdgeKey)
                this.startEdgePulse(step.fromId, step.toId)
                break
            }

            case "enqueue-course": {
                this.activeCourseId = step.courseId
                if (!this.courseQueue.includes(step.courseId)) {
                    this.courseQueue.push(step.courseId)
                }
                this.availableCourseIds.add(step.courseId)
                break
            }

            case "complete-course": {
                this.activeCourseId = step.courseId
                this.completedCourseIds.add(step.courseId)
                if (!this.topologicalOrder.includes(step.courseId)) {
                    this.topologicalOrder.push(step.courseId)
                }
                break
            }

            case "complete": {
                this.activeCourseId = null
                this.activeEdgeKey = null
                this.courseQueue = []
                this.canFinish = step.canFinish
                break
            }

            default:
                break
        }
    }

    onResetPlayback() {
        this.resetAnimationState()
        this.advanceStep()
    }

    onStepForward() {
        this.advanceStep()
    }

    startEdgePulse(fromId, toId) {
        this.edgePulseState.active = true
        this.edgePulseState.elapsed = 0
        this.edgePulseState.from.copy(this.coursePositions.get(fromId))
        this.edgePulseState.to.copy(this.coursePositions.get(toId))
    }

    update(delta) {
        if (!this.graphRoot || !this.currentStep) {
            return
        }

        if (this.camera.aspect !== this.lastBackdropAspect) {
            this.updateBackdropLayout()
        }

        const playbackDelta = this.isPlaying ? delta : 0
        this.presentationTime += playbackDelta
        this.currentStepElapsed += playbackDelta

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep()
            if (!advanced) {
                this.pause()
            }
        }

        this.updateGraphMotion(this.presentationTime)
        this.updateNodes(this.presentationTime)
        this.updateEdges()
        this.updateLabels()
        this.updateIndegreeBadges(this.presentationTime)
        this.updateActiveRing(this.presentationTime)
        this.updateEdgePulse(playbackDelta)
    }

    updateGraphMotion(elapsed) {
        this.graphRoot.rotation.y = Math.sin(elapsed * 0.18) * 0.05
        this.graphRoot.rotation.x = Math.cos(elapsed * 0.16) * 0.018
        this.graphRoot.position.y = Math.sin(elapsed * 0.55) * 0.05
    }

    updateNodes(elapsed) {
        this.courseNodes.forEach((node, courseId) => {
            const isActive = courseId === this.activeCourseId
            const isCompleted = this.completedCourseIds.has(courseId)
            const isAvailable = this.availableCourseIds.has(courseId)
            const isLocked = !isCompleted && !isAvailable && this.remainingIndegrees[courseId] > 0

            let targetColor = NODE_IDLE_COLOR
            let targetScale = 1
            let targetEmissiveIntensity = 0.18

            if (isLocked) {
                targetColor = NODE_LOCKED_COLOR
                targetEmissiveIntensity = 0.12
            }

            if (isAvailable) {
                targetColor = NODE_AVAILABLE_COLOR
                targetScale = 1.08
                targetEmissiveIntensity = 0.5
            }

            if (isCompleted) {
                targetColor = NODE_COMPLETE_COLOR
                targetScale = 1.04
                targetEmissiveIntensity = 0.38
            }

            if (isActive) {
                targetColor = NODE_ACTIVE_COLOR
                targetScale = 1.18 + Math.sin(elapsed * 4) * 0.03
                targetEmissiveIntensity = 0.85
            }

            const bob = Math.sin(elapsed * 1.6 + courseId * 0.7) * 0.03
            node.position.y = THREE.MathUtils.lerp(
                node.position.y,
                node.userData.baseY + bob,
                0.12
            )

            const nextScale = THREE.MathUtils.lerp(node.scale.x, targetScale, 0.14)
            node.scale.setScalar(nextScale)
            node.material.color.lerp(targetColor, 0.14)
            node.material.emissive.lerp(targetColor, 0.14)
            node.material.emissiveIntensity = THREE.MathUtils.lerp(
                node.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.14
            )
        })
    }

    updateEdges() {
        this.courseEdges.forEach((edgeGroup, nextEdgeKey) => {
            const { line, cone } = edgeGroup.userData
            const isActive = nextEdgeKey === this.activeEdgeKey
            const isResolved = this.resolvedEdgeKeys.has(nextEdgeKey)

            let targetColor = EDGE_IDLE_COLOR
            let targetOpacity = 0.55
            let targetEmissiveIntensity = 0.16

            if (isResolved) {
                targetColor = EDGE_RESOLVED_COLOR
                targetOpacity = 0.86
                targetEmissiveIntensity = 0.32
            }

            if (isActive) {
                targetColor = EDGE_ACTIVE_COLOR
                targetOpacity = 0.98
                targetEmissiveIntensity = 0.68
            }

            line.material.color.lerp(targetColor, 0.18)
            line.material.opacity = THREE.MathUtils.lerp(
                line.material.opacity,
                targetOpacity,
                0.18
            )
            cone.material.color.lerp(targetColor, 0.18)
            cone.material.emissive.lerp(targetColor, 0.18)
            cone.material.emissiveIntensity = THREE.MathUtils.lerp(
                cone.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.18
            )
        })
    }

    updateLabels() {
        this.courseLabels.forEach((label, courseId) => {
            const isActive = this.activeCourseId === courseId
            const textColor = isActive ? "#000000" : "#ffffff"
            const node = this.courseNodes.get(courseId)

            updateTextSprite(label, courseId, {
                fontSize: 32,
                textColor,
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: isActive
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(2, 6, 23, 1)",
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
            })

            const targetPosition = node.position.clone().add(new THREE.Vector3(0, 0, 0))
            label.position.lerp(targetPosition, 0.2)
        })
    }

    updateIndegreeBadges(elapsed) {
        this.indegreeBadges.forEach((badge, courseId) => {
            const node = this.courseNodes.get(courseId)
            const value = this.remainingIndegrees[courseId]
            const isAvailable = this.availableCourseIds.has(courseId)
            const isCompleted = this.completedCourseIds.has(courseId)
            const isActive = this.activeCourseId === courseId

            let fillStyle = "rgba(32, 32, 32, 0.96)"
            let textColor = "#dbeafe"
            let strokeStyle = "rgba(255, 255, 255, 0.16)"
            let outlineColor = "rgba(0, 0, 0, 0.96)"
            let outlineWidth = 9

            if (value === 0) {
                fillStyle = "rgba(17, 94, 89, 0.98)"
                textColor = "#ecfeff"
                strokeStyle = "rgba(153, 246, 228, 0.34)"
            }

            if (isAvailable) {
                fillStyle = "rgba(30, 64, 175, 0.98)"
                textColor = "#eff6ff"
                strokeStyle = "rgba(147, 197, 253, 0.42)"
            }

            if (isCompleted) {
                fillStyle = "rgba(6, 78, 59, 0.98)"
                textColor = "#ecfdf5"
                strokeStyle = "rgba(110, 231, 183, 0.34)"
            }

            if (isActive) {
                fillStyle = "rgba(10, 10, 10, 0.98)"
                textColor = "#ffffff"
                strokeStyle = "rgba(255, 255, 255, 0.22)"
                outlineColor = "rgba(0, 0, 0, 0.98)"
                outlineWidth = 10
            }

            updateTextSprite(badge, value, {
                fontSize: 30,
                textColor,
                fillStyle,
                strokeStyle,
                outlineColor,
                outlineWidth,
                shadowColor: "rgba(0, 0, 0, 0.45)",
                shadowBlur: 7,
            })

            const pulse = isActive ? 0.08 + Math.sin(elapsed * 5.2) * 0.035 : 0
            const targetPosition = node.position.clone().add(new THREE.Vector3(0, 0.84 + pulse, 0))
            badge.position.lerp(targetPosition, 0.18)
        })
    }

    updateActiveRing(elapsed) {
        if (this.activeCourseId === null || !this.courseNodes.has(this.activeCourseId)) {
            this.activeRing.material.opacity = THREE.MathUtils.lerp(
                this.activeRing.material.opacity,
                0,
                0.18
            )
            return
        }

        const activeNode = this.courseNodes.get(this.activeCourseId)
        this.activeRing.position.lerp(
            activeNode.position.clone().add(new THREE.Vector3(0, -0.34, 0)),
            0.18
        )
        this.activeRing.scale.setScalar(1 + Math.sin(elapsed * 4.5) * 0.08)
        this.activeRing.material.opacity = THREE.MathUtils.lerp(
            this.activeRing.material.opacity,
            0.9,
            0.18
        )
    }

    updateEdgePulse(delta) {
        const state = this.edgePulseState

        if (!state.active) {
            this.edgePulse.material.opacity = THREE.MathUtils.lerp(
                this.edgePulse.material.opacity,
                0,
                0.18
            )
            return
        }

        state.elapsed += delta
        const t = Math.min(state.elapsed / state.duration, 1)
        const eased = THREE.MathUtils.smoothstep(t, 0, 1)
        this.edgePulse.position.lerpVectors(state.from, state.to, eased)
        this.edgePulse.material.opacity = 0.9 * Math.sin(eased * Math.PI)
        this.edgePulse.scale.setScalar(1 + Math.sin(eased * Math.PI) * 0.5)

        if (t >= 1) {
            state.active = false
        }
    }

    onDispose() {
        const disposeSpriteTexture = (sprite) => {
            if (!sprite || !sprite.material || !sprite.material.map) {
                return
            }

            sprite.material.map.dispose()
        }

        this.courseLabels.forEach(disposeSpriteTexture)
        this.indegreeBadges.forEach(disposeSpriteTexture)
    }
}